import billingDataState from '@/states/billingDataState';
import { Constants } from '@/utils/constants';
import Router from 'next/router';
import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getPaymentStatusText, getPaymentTypeText } from '../../utils/common';
import url from '../../utils/config';

type Props = {
  isOpen: boolean;
  // eslint-disable-next-line react/require-default-props
  isMobile?: boolean;
};

/**
 * 請求一覧をカルーセルで見せ、対象月に応じて請求明細画面または請求書画面へ振り分ける一覧部品。
 * モバイルと固定で遷移先が分かれるため、この部品内で月判定と画面種別判定をまとめている。
 */
const Billing = ({ isOpen, isMobile = false }: Props) => {
  const billingData = useRecoilValue(billingDataState);
  const [activeIndex, setActiveIndex] = useState(
    billingData.billing_list.length > 0 ? billingData.billing_list.length - 1 : 0
  );

  // eslint-disable-next-line consistent-return
  const handleClickBilldetailButton = (target_month: string) => {
    // 請求体系の切替月を境に、旧明細画面と新請求書画面を出し分ける。
    if (isMobile) {
      if (target_month < Constants.DateOfSwitching.INVOICE) {
        Router.push({ pathname: '/mobile/top/billing', query: { month: target_month } });
        return;
      }
      Router.push({ pathname: '/mobile/top/invoice', query: { month: target_month } });
      return;
    }

    if (target_month < Constants.DateOfSwitching.INVOICE) {
      Router.push({ pathname: '/top/billing', query: { month: target_month } });
    } else {
      Router.push({ pathname: '/top/invoice', query: { month: target_month } });
    }
  };

  if (billingData.billing_list.length) {
    // 最新月を先頭に見せたいので、API 配列を反転してカルーセルへ渡す。
    const reversedList = [...billingData.billing_list].reverse();
    return (
      <Swiper
        modules={[Navigation]}
        loop={false}
        effect='fade'
        fadeEffect={{ crossFade: true }}
        spaceBetween={100}
        slidesPerView={1.15}
        navigation={{ nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' }}
        centeredSlides
        breakpoints={{ 639: { slidesPerView: 1.08 } }}
        wrapperTag='ul'
        initialSlide={reversedList.length - 1}
        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
      >
        {reversedList.map((data, idx) => (
          <SwiperSlide className='m-carousel__item' key={data.billing_month} tag='li'>
            <div className='m-carousel__content'>
              <p className='m-text'>
                <b>{`${data.target_month.replace('-0', '年').replace('-', '年')}月ご利用料金`}</b>
              </p>
              <p className='m-carousel__priceTxt'>{`${data.price_tax_included.toLocaleString()}円`}</p>
              <div className='m-carousel__payment'>
                <p className='m-text'>{`お支払い方法：${getPaymentTypeText(
                  data.payment_type
                )} ／ お支払い状況：${getPaymentStatusText(data.status_code)}`}</p>
                <button
                  className='m-carousel__linkTxt'
                  onClick={() => {
                    handleClickBilldetailButton(data.target_month.replace('-', ''));
                  }}
                  tabIndex={activeIndex === idx ? 0 : -1}
                  aria-hidden={activeIndex === idx ? undefined : 'true'}
                >
                  <img
                    className='m-carousel__linkImg'
                    src={url('/images/icon_arrow_right.svg')}
                    alt=''
                  />
                  明細を表示する
                </button>
              </div>
            </div>
          </SwiperSlide>
        ))}
        <button type='button' className='swiper-button-prev' aria-label='前へ' tabIndex={0} />
        <button type='button' className='swiper-button-next' aria-label='次へ' tabIndex={0} />
      </Swiper>
    );
  }

  if (isOpen) {
    return <p className='m-text'>ご参照可能なご利用料金はありません</p>;
  }

  return <p className='m-text'>回線開通後にこちらにご利用料金が表示されます</p>;
};

export default Billing;
